<template>
  <div v-if="returnUser" class="impersonation-banner">
    <div class="flex items-center justify-center gap-3 py-2 px-4">
      <i class="pi pi-eye text-sm"></i>
      <span class="text-sm font-medium">
        {{ t('impersonation.banner', { name: returnUser.name || returnUser.preferred_username }) }}
      </span>
      <Button :label="t('impersonation.jumpBack')" icon="pi pi-arrow-left" size="small" severity="contrast"
        :loading="loading" @click="jumpBack" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { AuthApi } from '@/api/auth/auth';
import { decodeJwtPayload } from '@/lib/utils';

const { t } = useI18n();
const loading = ref(false);

const STORAGE_KEY = 'impersonation.returnToken';

interface ReturnUser {
  sub: string;
  name?: string;
  preferred_username?: string;
}

const returnUser = ref<ReturnUser | null>(null);
let expiryTimer: ReturnType<typeof setTimeout> | null = null;

function checkToken() {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    returnUser.value = null;
    return;
  }

  try {
    const payload = decodeJwtPayload(raw);
    if (payload.type !== 'impersonation-return' || !payload.sub) {
      cleanup();
      return;
    }
    const expiresMs = (payload.exp ?? 0) * 1000;
    if (Date.now() >= expiresMs) {
      cleanup();
      return;
    }

    returnUser.value = {
      sub: payload.sub,
      name: payload.name,
      preferred_username: payload.preferred_username,
    };

    // Auto-hide when the token expires.
    if (expiryTimer) clearTimeout(expiryTimer);
    expiryTimer = setTimeout(() => {
      cleanup();
    }, expiresMs - Date.now());
  } catch {
    cleanup();
  }
}

function cleanup() {
  localStorage.removeItem(STORAGE_KEY);
  returnUser.value = null;
  if (expiryTimer) {
    clearTimeout(expiryTimer);
    expiryTimer = null;
  }
}

async function jumpBack() {
  const token = localStorage.getItem(STORAGE_KEY);
  if (!token) {
    cleanup();
    return;
  }

  loading.value = true;
  try {
    const { loginUrl } = await AuthApi.returnFromImpersonation(token);
    cleanup();
    localStorage.removeItem('accessToken');
    localStorage.setItem('impersonation.postReturnRedirect', '/admin/users');
    window.location.assign(loginUrl);
  } catch {
    cleanup();
  }
}

onMounted(() => checkToken());
onUnmounted(() => {
  if (expiryTimer) clearTimeout(expiryTimer);
});
</script>

<style scoped>
.impersonation-banner {
  background: var(--p-orange-500, #f59e0b);
  color: white;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 9999;
}
</style>
